import React, { useContext, useState } from 'react';
import styled from 'styled-components';
import { arrayOf, bool, string } from 'prop-types';

import { UserContext } from '../auth/hook/userContext';
import { useTranslate } from '../../translate/I18n';
import { assetize } from '../../utils/pureUtils';
import Box from '../atoms/Box';
import Button from '../atoms/Button';
import Card from '../atoms/Card';
import Text from '../atoms/Text';
import StatusTag from '../molecules/StatusTag';
import Clock from '../atoms/icons/Clock';
import ExternalLink from '../atoms/icons/ExternalLink';
import Modal from '../molecules/Modal';
import PartnerCategory from './PartnerCategory';
import SanitizedHTML from '../elements/SanitizedHTML';

const StyledLogo = styled('img')(({ theme }) => ({
  display: 'inline-block',
  maxWidth: '100px',
  maxHeight: '100px',
  objectFit: 'contain',
  marginRight: '32px',
  marginBottom: '12px',
  [`@media (max-width: ${theme.breakpoints.lg})`]: {
    marginBottom: theme.space[2],
    marginLeft: 'auto',
    marginRight: 'auto'
  }
}));

const StyledIllustration = styled('img')(({ theme }) => ({
  margin: 'auto',
  height: 'auto',
  width: '100%',
  display: 'none',
  [`@media (min-width: ${theme.breakpoints.lg})`]: {
    display: 'block'
  }
}));

const StyledButton = styled(Button)({
  position: 'absolute',
  top: '10px',
  right: '10px'
});

const StyledNotificationBullet = styled('span')(({ theme }) => ({
  width: '8px',
  height: '8px',
  display: 'inline-block',
  borderRadius: '8px',
  marginLeft: '6px',
  marginTop: '6px',
  background: theme.colors.secondary
}));

const PartnerForLoggedUser = ({ id: camelCaseName, categories, hasNotification }) => {
  const user = useContext(UserContext);
  const { t } = useTranslate([`communityPartners_${camelCaseName}`]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [hasPartnerNotification, setHasPartnerNotification] = useState(hasNotification);
  const [hasPartnerRequest] = useState(null); // setHasPartnerRequest - if we store it in DB
  const userRole = user.getRole();
  const pathToPartnerTranslation = `communityPartners_${camelCaseName}:${userRole}`;
  const logoFilePath = t(`${pathToPartnerTranslation}:logoFilePath`);
  const illustrationFilePath = t(`${pathToPartnerTranslation}:details:illustrationFilePath`);

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const openModal = () => {
    // TODO: store in DB if user has already sent a request?
    if (!hasPartnerRequest) setIsModalOpen(true);
    setHasPartnerNotification(false);
  };

  const redirectToLink = () => {
    const link = t(`${pathToPartnerTranslation}:details:link`);
    window.open(link, '_blank');
    setIsModalOpen(false);
    // setHasPartnerRequest(true); // if we store it in DB (+ add call to API)
  };

  return (
    <>
      <Card
        data-cy={`${camelCaseName}-card`}
        onClick={openModal}
        cursor={hasPartnerRequest ? '' : 'pointer'}
        height="100%"
        overflow="visible"
        position="relative">
        {hasPartnerNotification && <StyledNotificationBullet data-cy={`notification-${camelCaseName}`} />}
        <Box py={3} px={5}>
          <Box display="flex" flexDirection={{ _: 'column', lg: 'row' }}>
            <StyledLogo src={assetize(`/${logoFilePath}`)} alt={`${camelCaseName} logo`} />
            <Box>
              <Text styleName="pH3" mb={1} pr={4}>
                {t(`${pathToPartnerTranslation}:overview:title`)}
                {categories.map(category => (
                  <PartnerCategory key={category} category={category.toUpperCase()} />
                ))}
              </Text>
              <Text color="secondary" mb={3} fontWeight="700">
                {t(`${pathToPartnerTranslation}:overview:offer`)}
              </Text>
            </Box>
          </Box>
          {!hasPartnerRequest && (
            <StyledButton p={1} pl={2} data-cy={`${camelCaseName}-offers-button`} styleType="tertiary">
              <ExternalLink />
            </StyledButton>
          )}
          <Text>{t(`${pathToPartnerTranslation}:overview:description`)}</Text>
          {hasPartnerRequest && (
            <Box pt={3}>
              <StatusTag>
                <Box display="flex" alignItems="center">
                  <Clock color="secondary" fontSize="22px" />
                  <Box ml={2} display="inline-block">
                    <Text styleName="pLabelWarning">
                      {t(`${pathToPartnerTranslation}:overview:alreadySentRequest`)}
                    </Text>
                  </Box>
                </Box>
              </StatusTag>
            </Box>
          )}
        </Box>
      </Card>
      <Modal isOpen={isModalOpen} close={closeModal}>
        <Box
          py={3}
          px={5}
          display="flex"
          flexDirection={{ _: 'column', lg: 'row' }}
          alignItems={{ _: 'start' }}
          data-cy={`${camelCaseName}-modal`}>
          <StyledLogo src={assetize(`/${logoFilePath}`)} alt={`${camelCaseName} logo`} />
          <Box>
            <Text styleName="pH3" mb={1}>
              {t(`${pathToPartnerTranslation}:details:title`)}
              {categories.map(category => (
                <PartnerCategory key={category} category={category.toUpperCase()} />
              ))}
            </Text>
            <SanitizedHTML content={t(`${pathToPartnerTranslation}:details:description`)} />
            <Button data-cy={`link-community-${camelCaseName}`} mt={4} styleType="tertiary" onClick={redirectToLink}>
              {t(`${pathToPartnerTranslation}:details:requestCTA`)}
            </Button>
          </Box>
        </Box>
        {illustrationFilePath && (
          <StyledIllustration
            src={assetize(`/${illustrationFilePath}`)}
            alt={t(`${pathToPartnerTranslation}:details:title`)}
          />
        )}
      </Modal>
    </>
  );
};

PartnerForLoggedUser.propTypes = {
  id: string,
  categories: arrayOf(string),
  hasNotification: bool
};

export default PartnerForLoggedUser;
